/**
 * Admin panel container file
 *
 * @package    src/containers
 * @author     WangTuring <wangwang@turing.com>
 * @copyright  2018 Turing Company
 * @license    Turing License
 * @version    2.0
 * @link       https://turing.ly/admin
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { Switch, Route, Redirect, NavLink } from 'react-router-dom';
import adminPanelRoutes from '../../routes/AdminPanelRoutes';
import { Divider } from '@material-ui/core';

import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { checkIfRoleCan } from '../../constants/RoleActions';

// imports

import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import { GB_FLAGS, withLDFlagsFromBackend } from '../../legacy_components/services/launchDarkly';
import ShowHiddenPageWarning from './ShowHiddenPageWarning';
// import { Row, Col } from 'antd';
/**
 * Drawer width in left side
 */
const drawerWidth = 250;

/**
 * Component style sheets
 */
const styles = theme => ({
  root: {
    display: 'flex'
  },
  toolbar: {
    paddingRight: 24 // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  menuButton: {
    marginLeft: 12,
    marginRight: 36
  },
  menuButtonHidden: {
    display: 'none'
  },
  brand: {
    flexGrow: 1,
    paddingLeft: theme.spacing.unit * 9,
    color: 'grey'
  },
  title: {
    flexGrow: 1
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.spacing.unit * 7,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing.unit * 9
    }
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    padding: theme.spacing.unit * 3,
    height: '100vh',
    overflow: 'auto'
  },
  chartContainer: {
    marginLeft: -22
  },
  tableContainer: {
    height: 320
  },
  h5: {
    marginBottom: theme.spacing.unit * 2
  },
  item: {
    textDecoration: 'none',
    color: 'grey'
  },
  listItem: {
    backgroundColor: 'white'
  },
  activeListItem: {
    backgroundColor: '#ebebeb'
  }
});

/**
 * Admin panel container class
 *
 * This class will be contains admin panel pages
 *
 * @category  Container
 * @author    WangTuring <wangwang@turing.com>
 */
class AdminPanelContainer extends Component {
  /**
   * Default constructor
   */
  constructor (props) {
    super(props);

    this.state = {
      open: true,
      menuFilterText: null,
      showModal: false
    };
  }

  onMenuFilterTextChange = (e) => {
    this.setState({
      menuFilterText: e.target.value
    });
  }

  /**
   * Handle function for open drawer
   */
  handleDrawerOpen = () => {
    this.setState({
      open: true
    });
  }

  /**
   * Handle function for close drawer
   */
  handleDrawerClose = () => {
    this.setState({
      open: false
    });
  }

  /**
   * Handle function for check if current active is active or not
   */
  isActiveRoute = (routeName) => {
    return this.props.location.pathname.indexOf(routeName) > -1 ? true : false;
  }

  /**
   * Get header label for current page
   */
  getHeaderLabel = () => {
    let label = 'Admin Panel';

    adminPanelRoutes.forEach(route => {
      if (route.path === this.props.location.pathname) {
        label = route.navbarName;
      }
    });

    return label;
  }

  /**
   * Render function to view this component
   */
  render () {
    const { classes, LDFlags } = this.props;
    const { menuFilterText } = this.state;
    let pathKey = "";
    const hide_admin_routes = LDFlags?.hide_admin_routes || [];
    Object.values(this.props.match.params).map(value => {
      pathKey = `${pathKey}/${value}`;
    });
      // Normalize paths to ignore slashes
    const normalize = (str) => str.split('/').filter(Boolean).join(' ');
    const switchRoutes = (
      <Switch>
        {adminPanelRoutes.filter(item => (!item.roleAction || checkIfRoleCan(item.roleAction))).map((prop, key) => {
          const isHidden = hide_admin_routes.some(route => normalize(route) === normalize(prop.path));
  
          if (isHidden) return <Route path={prop.path} component={ShowHiddenPageWarning} key={key} />;
  
          if (prop.redirect) return <Redirect from={prop.path} to={prop.to} key={key} />;
          
          return <Route path={prop.path} component={prop.component} key={key} />;
        })}
      </Switch>
    );
    const navLists = (
      <List>
        {adminPanelRoutes.filter(item => (!item.roleAction || checkIfRoleCan(item.roleAction)) && (menuFilterText == null || item.sidebarName.toLowerCase().indexOf(menuFilterText.toLowerCase()) > -1)).map((route, key) => {
          const isHidden = hide_admin_routes.some(route_ => normalize(route_) === normalize(route.path));
          if (isHidden) return null
          return (
            <NavLink to={route.path} className={classes.item} key={key} {...route.navLinkProps}>
              <ListItem button className={this.isActiveRoute(route.path) ? classes.activeListItem : classes.listItem}>
                <ListItemIcon>
                  <route.icon />
                </ListItemIcon>
                <ListItemText primary={route.sidebarName} />
              </ListItem>
            </NavLink>
          );
        })}
      </List>
    );

    return (
      <div className={classes.root}>
        <CssBaseline />
        <AppBar
          style={{ zIndex: 999 }}
          position="absolute"
          className={classNames(classes.appBar, this.state.open && classes.appBarShift)}
        >
          <Toolbar disableGutters={!this.state.open} className={classes.toolbar}>
            <IconButton
              color="inherit"
              aria-label="Open drawer"
              onClick={this.handleDrawerOpen}
              className={classNames(
                classes.menuButton,
                this.state.open && classes.menuButtonHidden,
              )}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              className={classes.title}
            >
              {this.getHeaderLabel()}
            </Typography>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          classes={{
            paper: classNames(classes.drawerPaper, !this.state.open && classes.drawerPaperClose),
          }}
          open={this.state.open}
        >
          <div className={classes.toolbarIcon}>
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              className={classes.brand}
            >
              Turing
            </Typography>
            <IconButton onClick={this.handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <Divider />
          <div style={{ margin: '0 16px' }}>
            <TextField
              label="Menu Item Filter"
              onChange={this.onMenuFilterTextChange}
              style={{ width: '100%' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>
          {navLists}
        </Drawer>
        <main className={classes.content}>
          <div className={classes.appBarSpacer} />
          <div key={pathKey}>{switchRoutes}</div>
        </main>
      </div>
    );
  }
}

AdminPanelContainer.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(withLDFlagsFromBackend(AdminPanelContainer, [GB_FLAGS.hide_admin_routes], true));
